import React, { useEffect } from 'react';
import FormInput from './FormInput';
import FormTextArea from './FormTextArea';
import FormRadio from './FormRadio';
import FormCheckbox from './FormCheckbox';
import FormSelect from './FormSelect';
import SearchableDiagnosisDropdown from './SearchableDiagnosisDropdown';
import { US_STATES, calculateAge, validateZipCode, formatSSN } from './formUtils';

const OMHCReferralForm = ({ formData, onDataUpdate, validationErrors }) => {
  const clientType = formData.clientType || 'Adult';
  const omhcData = formData.omhc || {};
  const errors = validationErrors || {};

  const handleChange = (field, value) => {
    onDataUpdate({
      omhc: {
        ...omhcData,
        [field]: value
      }
    });
  };

  // Handle multiple field updates at once (for diagnosis selection)
  const handleMultiChange = (updates) => {
    onDataUpdate({
      omhc: {
        ...omhcData,
        ...updates
      }
    });
  };

  const handleCheckboxChange = (field, value) => {
    const current = omhcData[field] || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    handleChange(field, updated);
  };

  // Auto-calculate age when DOB changes
  useEffect(() => {
    if (omhcData.dob) {
      const age = calculateAge(omhcData.dob);
      if (age !== omhcData.age) {
        handleChange('age', age);
      }
    }
  }, [omhcData.dob]);

  return (
    <div className="space-y-8">
      {/* Header with Client Type Indicator */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border-l-4 border-blue-600">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">OMHC Referral Form</h1>
            <p className="text-sm text-gray-600 mt-1">Outpatient Mental Health Clinic</p>
          </div>
          <div className="bg-blue-600 text-white px-4 py-2 rounded-full font-bold text-sm">
            Client Type: {clientType}
          </div>
        </div>
      </div>

      {/* Client Demographics */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">
          Client Demographics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <FormInput
            label="Last Name"
            value={omhcData.lastName}
            onChange={(value) => handleChange('lastName', value)}
            required
          />
          <FormInput
            label="First Name"
            value={omhcData.firstName}
            onChange={(value) => handleChange('firstName', value)}
            required
          />
          <FormInput
            label="Middle Name"
            value={omhcData.middleName}
            onChange={(value) => handleChange('middleName', value)}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Preferred Name"
            value={omhcData.preferredName}
            onChange={(value) => handleChange('preferredName', value)}
          />
          <FormInput
            label="Date of Birth"
            type="date"
            value={omhcData.dob}
            onChange={(value) => handleChange('dob', value)}
            required
          />
          <FormInput
            label="Age"
            type="number"
            value={omhcData.age}
            onChange={(value) => handleChange('age', value)}
            disabled
            helperText="Auto-calculated from Date of Birth"
          />
          <FormSelect
            label="Sex Assigned at Birth"
            value={omhcData.sexAssigned}
            onChange={(value) => handleChange('sexAssigned', value)}
            options={['Male', 'Female', 'Intersex', 'Prefer not to say']}
          />
          <FormSelect
            label="Gender Identity"
            value={omhcData.genderIdentity}
            onChange={(value) => handleChange('genderIdentity', value)}
            options={[
              'Male',
              'Female',
              'Transgender Male',
              'Transgender Female',
              'Non-binary',
              'Gender Fluid',
              'Prefer to self-describe',
              'Prefer not to say'
            ]}
          />
          <FormSelect
            label="Pronouns"
            value={omhcData.pronouns}
            onChange={(value) => handleChange('pronouns', value)}
            options={[
              'He/Him/His',
              'She/Her/Hers',
              'They/Them/Theirs',
              'Ze/Hir/Hirs',
              'Other (please specify)'
            ]}
          />
          {omhcData.pronouns === 'Other (please specify)' && (
            <FormInput
              label="Other Pronouns"
              value={omhcData.otherPronouns}
              onChange={(value) => handleChange('otherPronouns', value)}
            />
          )}
        </div>
      </section>

      {/* Insurance & Identification */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">
          Insurance & Identification
        </h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="SSN"
              value={omhcData.ssn}
              onChange={(value) => handleChange('ssn', formatSSN(value))}
              placeholder="XXX-XX-XXXX"
              helperText="Format: 123-45-6789 (9 digits)"
            />
          </div>
          <FormSelect
            label="Insurance Type"
            value={omhcData.insuranceType}
            onChange={(value) => handleChange('insuranceType', value)}
            options={['Medicaid', 'Medicare', 'Self Pay']}
            required
          />
          {omhcData.insuranceType && omhcData.insuranceType !== 'Self Pay' && (
            <FormInput
              label={`${omhcData.insuranceType} ID Number`}
              value={omhcData.insuranceIdNumber}
              onChange={(value) => handleChange('insuranceIdNumber', value)}
            />
          )}
        </div>
      </section>

      {/* Contact Information */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">
          Contact Information
        </h2>
        {errors.omhcRequired && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            Please complete all required contact fields before proceeding.
          </div>
        )}
        <div className="space-y-4">
          {/* Home Address */}
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Home Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <FormInput
                label="Address Line 1"
                value={omhcData.homeAddressLine1}
                onChange={(value) => handleChange('homeAddressLine1', value)}
                required
                placeholder="Street address"
                error={errors.omhcHomeAddress}
              />
            </div>
            <div className="md:col-span-2">
              <FormInput
                label="Address Line 2"
                value={omhcData.homeAddressLine2}
                onChange={(value) => handleChange('homeAddressLine2', value)}
                placeholder="Apt, Suite, Unit, etc. (optional)"
              />
            </div>
            <FormInput
              label="City"
              value={omhcData.homeCity}
              onChange={(value) => handleChange('homeCity', value)}
              required
              error={errors.omhcHomeCity}
            />
            <FormSelect
              label="State"
              value={omhcData.homeState}
              onChange={(value) => handleChange('homeState', value)}
              options={US_STATES}
              required
              error={errors.omhcHomeState}
            />
            <FormInput
              label="Zip Code"
              value={omhcData.homeZip}
              onChange={(value) => handleChange('homeZip', value)}
              required
              placeholder="e.g., 21208"
              error={errors.omhcHomeZip || (omhcData.homeZip ? validateZipCode(omhcData.homeZip) : '')}
            />
          </div>

          {/* Mailing Address Toggle */}
          <div className="mt-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={omhcData.mailingAddressDifferent || false}
                onChange={(e) => handleChange('mailingAddressDifferent', e.target.checked)}
                className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500 rounded"
                data-testid="mailing-address-toggle"
              />
              <span className="text-sm font-medium text-gray-700">Mailing address is different from home address</span>
            </label>
          </div>

          {/* Mailing Address Fields */}
          {omhcData.mailingAddressDifferent && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg" data-testid="mailing-address-fields">
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Mailing Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <FormInput
                    label="Address Line 1"
                    value={omhcData.mailAddressLine1}
                    onChange={(value) => handleChange('mailAddressLine1', value)}
                    required
                    placeholder="Street address"
                  />
                </div>
                <div className="md:col-span-2">
                  <FormInput
                    label="Address Line 2"
                    value={omhcData.mailAddressLine2}
                    onChange={(value) => handleChange('mailAddressLine2', value)}
                    placeholder="Apt, Suite, Unit, etc. (optional)"
                  />
                </div>
                <FormInput
                  label="City"
                  value={omhcData.mailCity}
                  onChange={(value) => handleChange('mailCity', value)}
                  required
                />
                <FormSelect
                  label="State"
                  value={omhcData.mailState}
                  onChange={(value) => handleChange('mailState', value)}
                  options={US_STATES}
                  required
                />
                <FormInput
                  label="Zip Code"
                  value={omhcData.mailZip}
                  onChange={(value) => handleChange('mailZip', value)}
                  required
                  placeholder="e.g., 21208"
                  error={omhcData.mailZip ? validateZipCode(omhcData.mailZip) : ''}
                />
              </div>
            </div>
          )}

          {/* Phone, Email, Language */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Phone"
              type="tel"
              value={omhcData.clientPhone}
              onChange={(value) => handleChange('clientPhone', value)}
              required
              error={errors.omhcPhone}
            />
            <FormInput
              label="Email"
              type="email"
              value={omhcData.clientEmail}
              onChange={(value) => handleChange('clientEmail', value)}
            />
            <FormSelect
              label="Preferred Language"
              value={omhcData.preferredLanguage}
              onChange={(value) => handleChange('preferredLanguage', value)}
              options={['English', 'Spanish', 'French', 'Other']}
            />
            <FormRadio
              label="Need Interpreter?"
              name="needInterpreter"
              options={['Yes', 'No']}
              value={omhcData.needInterpreter}
              onChange={(value) => handleChange('needInterpreter', value)}
            />
          </div>
        </div>
      </section>

      {/* Demographics */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">
          Additional Demographics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormSelect
            label="Race"
            value={omhcData.race}
            onChange={(value) => handleChange('race', value)}
            options={[
              'American Indian or Alaska Native',
              'Asian',
              'Black or African American',
              'Hispanic or Latino',
              'Native Hawaiian or Pacific Islander',
              'White',
              'Two or More Races',
              'Prefer not to say'
            ]}
          />
          <FormSelect
            label="Ethnicity"
            value={omhcData.ethnicity}
            onChange={(value) => handleChange('ethnicity', value)}
            options={['Hispanic or Latino', 'Not Hispanic or Latino', 'Prefer not to say']}
          />
          <FormSelect
            label="Marital Status"
            value={omhcData.maritalStatus}
            onChange={(value) => handleChange('maritalStatus', value)}
            options={[
              'Single',
              'Married',
              'Divorced',
              'Separated',
              'Widowed',
              'Domestic Partnership',
              'Prefer not to say'
            ]}
          />
          <FormSelect
            label="Living Situation"
            value={omhcData.livingSituation}
            onChange={(value) => handleChange('livingSituation', value)}
            options={[
              'Own home',
              'Rent',
              'With family',
              'Group home',
              'Shelter',
              'Homeless',
              'Other'
            ]}
          />
          <FormSelect
            label={clientType === 'Minor' ? 'School Status' : 'Employment Status'}
            value={omhcData.employmentStatus}
            onChange={(value) => handleChange('employmentStatus', value)}
            options={
              clientType === 'Minor'
                ? ['Attending school', 'Home schooled', 'Not attending', 'Other']
                : ['Full-time', 'Part-time', 'Unemployed', 'Student', 'Retired', 'Disabled', 'Other']
            }
          />
        </div>
      </section>

      {/* Emergency Contact */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">
          Emergency Contact
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Emergency Contact Name"
            value={omhcData.emergencyContactName}
            onChange={(value) => handleChange('emergencyContactName', value)}
          />
          <FormInput
            label="Relationship to Client"
            value={omhcData.emergencyRelationship}
            onChange={(value) => handleChange('emergencyRelationship', value)}
          />
          <FormInput
            label="Emergency Contact Phone"
            type="tel"
            value={omhcData.emergencyPhone}
            onChange={(value) => handleChange('emergencyPhone', value)}
          />
        </div>
      </section>

      {/* MINOR-SPECIFIC SECTION */}
      {clientType === 'Minor' && (
        <section className="bg-purple-50 p-6 rounded-lg border-2 border-purple-300">
          <h2 className="text-xl font-bold text-purple-900 mb-4 border-b-2 border-purple-600 pb-2">
            Parent / Guardian Information (Minor Only)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Parent / Guardian Name"
              value={omhcData.parentGuardianName}
              onChange={(value) => handleChange('parentGuardianName', value)}
              required
            />
            <FormInput
              label="Relationship to Minor"
              value={omhcData.parentRelationship}
              onChange={(value) => handleChange('parentRelationship', value)}
              required
            />
            <FormSelect
              label="Custody Status"
              value={omhcData.custodyStatus}
              onChange={(value) => handleChange('custodyStatus', value)}
              options={[
                'Biological Parent',
                'Adoptive Parent',
                'Legal Guardian',
                'Foster Parent',
                'Kinship Care',
                'Other'
              ]}
              required
            />
            <FormRadio
              label="Legal Guardian Confirmed?"
              name="legalGuardianConfirmed"
              options={['Yes', 'No']}
              value={omhcData.legalGuardianConfirmed}
              onChange={(value) => handleChange('legalGuardianConfirmed', value)}
              required
            />
            <FormInput
              label="Additional Caregiver Contact"
              value={omhcData.additionalCaregiver}
              onChange={(value) => handleChange('additionalCaregiver', value)}
            />
            <FormInput
              label="School Name"
              value={omhcData.schoolName}
              onChange={(value) => handleChange('schoolName', value)}
            />
            <FormInput
              label="Grade"
              value={omhcData.grade}
              onChange={(value) => handleChange('grade', value)}
            />
            <FormRadio
              label="IEP / 504 Plan?"
              name="iepPlan"
              options={['Yes', 'No', 'Unknown']}
              value={omhcData.iepPlan}
              onChange={(value) => handleChange('iepPlan', value)}
            />
          </div>
        </section>
      )}

      {/* Requested Services */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">
          OMHC Requested Services
        </h2>
        <div className="space-y-2">
          <FormCheckbox
            label="Psychiatric Evaluation"
            checked={omhcData.requestedServices?.includes('Psychiatric Evaluation')}
            onChange={() => handleCheckboxChange('requestedServices', 'Psychiatric Evaluation')}
          />
          <FormCheckbox
            label="Medication Management"
            checked={omhcData.requestedServices?.includes('Medication Management')}
            onChange={() => handleCheckboxChange('requestedServices', 'Medication Management')}
          />
          <FormCheckbox
            label="Individual Therapy"
            checked={omhcData.requestedServices?.includes('Individual Therapy')}
            onChange={() => handleCheckboxChange('requestedServices', 'Individual Therapy')}
          />
          <FormCheckbox
            label="Family Therapy"
            checked={omhcData.requestedServices?.includes('Family Therapy')}
            onChange={() => handleCheckboxChange('requestedServices', 'Family Therapy')}
          />
          <FormCheckbox
            label="Group Therapy"
            checked={omhcData.requestedServices?.includes('Group Therapy')}
            onChange={() => handleCheckboxChange('requestedServices', 'Group Therapy')}
          />
          <FormCheckbox
            label="Diagnostic Assessment"
            checked={omhcData.requestedServices?.includes('Diagnostic Assessment')}
            onChange={() => handleCheckboxChange('requestedServices', 'Diagnostic Assessment')}
          />
          <FormCheckbox
            label="Care Coordination"
            checked={omhcData.requestedServices?.includes('Care Coordination')}
            onChange={() => handleCheckboxChange('requestedServices', 'Care Coordination')}
          />
          <FormCheckbox
            label="Other (please specify)"
            checked={omhcData.requestedServices?.includes('Other')}
            onChange={() => handleCheckboxChange('requestedServices', 'Other')}
          />
          {omhcData.requestedServices?.includes('Other') && (
            <FormInput
              label="Other Services"
              value={omhcData.otherServices}
              onChange={(value) => handleChange('otherServices', value)}
            />
          )}
        </div>
      </section>

      {/* Clinical / Diagnostic Information */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">
          Clinical / Diagnostic Information
        </h2>
        <div className="space-y-4">
          <SearchableDiagnosisDropdown
            label="Primary Diagnosis"
            value={omhcData.primaryDiagnosis}
            code={omhcData.primaryCode}
            onSelect={(name, code) => {
              handleMultiChange({ primaryDiagnosis: name, primaryCode: code });
            }}
            required
            helperText="Select a diagnosis — the ICD/DSM code will auto-populate"
          />

          <SearchableDiagnosisDropdown
            label="Secondary Diagnosis (optional)"
            value={omhcData.secondaryDiagnosis}
            code={omhcData.secondaryCode}
            onSelect={(name, code) => {
              handleMultiChange({ secondaryDiagnosis: name, secondaryCode: code });
            }}
            helperText="Optional — search and select a secondary diagnosis"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Diagnosis Date"
              type="date"
              value={omhcData.diagnosisDate}
              onChange={(value) => handleChange('diagnosisDate', value)}
            />
            <FormInput
              label="Diagnosed By"
              value={omhcData.diagnosedBy}
              onChange={(value) => handleChange('diagnosedBy', value)}
            />
            <FormSelect
              label="Diagnosis Severity"
              value={omhcData.diagnosisSeverity}
              onChange={(value) => handleChange('diagnosisSeverity', value)}
              options={['Mild', 'Moderate', 'Severe']}
            />
            <FormSelect
              label="Symptom Duration"
              value={omhcData.symptomDuration}
              onChange={(value) => handleChange('symptomDuration', value)}
              options={[
                'Less than 30 days',
                '1 to 6 months',
                '6 to 12 months',
                'More than 12 months'
              ]}
            />
            <FormSelect
              label="Treatment History"
              value={omhcData.treatmentHistory}
              onChange={(value) => handleChange('treatmentHistory', value)}
              options={['New to treatment', 'Intermittent', 'Ongoing', 'Recent discharge']}
            />
          </div>
        </div>
        <div className="mt-4 space-y-4">
          <FormTextArea
            label="Current Symptoms"
            value={omhcData.currentSymptoms}
            onChange={(value) => handleChange('currentSymptoms', value)}
            rows={3}
          />
          <FormTextArea
            label="Presenting Problem / Reason for Referral"
            value={omhcData.presentingProblem}
            onChange={(value) => handleChange('presentingProblem', value)}
            rows={3}
            required
          />
          <FormTextArea
            label="History of Treatment"
            value={omhcData.historyOfTreatment}
            onChange={(value) => handleChange('historyOfTreatment', value)}
            rows={3}
          />
          <FormTextArea
            label="Current Medications"
            value={omhcData.currentMedications}
            onChange={(value) => handleChange('currentMedications', value)}
            rows={3}
            helperText="List all current medications, dosages, and frequency"
          />
          <FormInput
            label="Allergies"
            value={omhcData.allergies}
            onChange={(value) => handleChange('allergies', value)}
          />
          <FormRadio
            label="Substance Use Concerns?"
            name="substanceUse"
            options={['Yes', 'No']}
            value={omhcData.substanceUse}
            onChange={(value) => handleChange('substanceUse', value)}
          />
          <FormTextArea
            label="Medical Comorbidities"
            value={omhcData.medicalComorbidities}
            onChange={(value) => handleChange('medicalComorbidities', value)}
            rows={2}
          />
        </div>
      </section>

      {/* Functional Impairment */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">
          Functional Impairment
        </h2>
        <div className="space-y-2 mb-4">
          <FormCheckbox
            label="Difficulty with daily living activities"
            checked={omhcData.impairmentAreas?.includes('Daily living')}
            onChange={() => handleCheckboxChange('impairmentAreas', 'Daily living')}
          />
          <FormCheckbox
            label="School impairment"
            checked={omhcData.impairmentAreas?.includes('School')}
            onChange={() => handleCheckboxChange('impairmentAreas', 'School')}
          />
          <FormCheckbox
            label="Work impairment"
            checked={omhcData.impairmentAreas?.includes('Work')}
            onChange={() => handleCheckboxChange('impairmentAreas', 'Work')}
          />
          <FormCheckbox
            label="Social impairment"
            checked={omhcData.impairmentAreas?.includes('Social')}
            onChange={() => handleCheckboxChange('impairmentAreas', 'Social')}
          />
          <FormCheckbox
            label="Family conflict"
            checked={omhcData.impairmentAreas?.includes('Family conflict')}
            onChange={() => handleCheckboxChange('impairmentAreas', 'Family conflict')}
          />
          <FormCheckbox
            label="Housing instability"
            checked={omhcData.impairmentAreas?.includes('Housing')}
            onChange={() => handleCheckboxChange('impairmentAreas', 'Housing')}
          />
          <FormCheckbox
            label="Community functioning impairment"
            checked={omhcData.impairmentAreas?.includes('Community')}
            onChange={() => handleCheckboxChange('impairmentAreas', 'Community')}
          />
          <FormCheckbox
            label="Medication adherence concerns"
            checked={omhcData.impairmentAreas?.includes('Medication adherence')}
            onChange={() => handleCheckboxChange('impairmentAreas', 'Medication adherence')}
          />
          <FormCheckbox
            label="Behavioral dysregulation"
            checked={omhcData.impairmentAreas?.includes('Behavioral')}
            onChange={() => handleCheckboxChange('impairmentAreas', 'Behavioral')}
          />
          <FormCheckbox
            label="Poor coping skills"
            checked={omhcData.impairmentAreas?.includes('Coping skills')}
            onChange={() => handleCheckboxChange('impairmentAreas', 'Coping skills')}
          />
          <FormCheckbox
            label="Frequent crises"
            checked={omhcData.impairmentAreas?.includes('Frequent crises')}
            onChange={() => handleCheckboxChange('impairmentAreas', 'Frequent crises')}
          />
          <FormCheckbox
            label="Other (please specify)"
            checked={omhcData.impairmentAreas?.includes('Other')}
            onChange={() => handleCheckboxChange('impairmentAreas', 'Other')}
          />
        </div>
        <FormRadio
          label="Functional Impairment Level"
          name="impairmentLevel"
          options={['Mild', 'Moderate', 'Marked', 'Severe']}
          value={omhcData.impairmentLevel}
          onChange={(value) => handleChange('impairmentLevel', value)}
          inline={true}
        />
      </section>

      {/* Risk / Safety Review */}
      <section className="bg-red-50 p-6 rounded-lg border-2 border-red-300">
        <h2 className="text-xl font-bold text-red-900 mb-4 border-b-2 border-red-600 pb-2">
          Risk / Safety Review
        </h2>
        <div className="space-y-2 mb-4">
          <FormCheckbox
            label="No known current risk"
            checked={omhcData.riskFactors?.includes('No known risk')}
            onChange={() => handleCheckboxChange('riskFactors', 'No known risk')}
          />
          <FormCheckbox
            label="Suicidal ideation"
            checked={omhcData.riskFactors?.includes('Suicidal ideation')}
            onChange={() => handleCheckboxChange('riskFactors', 'Suicidal ideation')}
          />
          <FormCheckbox
            label="Homicidal ideation"
            checked={omhcData.riskFactors?.includes('Homicidal ideation')}
            onChange={() => handleCheckboxChange('riskFactors', 'Homicidal ideation')}
          />
          <FormCheckbox
            label="Self-injurious behavior"
            checked={omhcData.riskFactors?.includes('Self-injury')}
            onChange={() => handleCheckboxChange('riskFactors', 'Self-injury')}
          />
          <FormCheckbox
            label="Aggression"
            checked={omhcData.riskFactors?.includes('Aggression')}
            onChange={() => handleCheckboxChange('riskFactors', 'Aggression')}
          />
          <FormCheckbox
            label="Elopement risk"
            checked={omhcData.riskFactors?.includes('Elopement')}
            onChange={() => handleCheckboxChange('riskFactors', 'Elopement')}
          />
          <FormCheckbox
            label="Psychosis symptoms"
            checked={omhcData.riskFactors?.includes('Psychosis')}
            onChange={() => handleCheckboxChange('riskFactors', 'Psychosis')}
          />
          <FormCheckbox
            label="Substance intoxication risk"
            checked={omhcData.riskFactors?.includes('Substance intoxication')}
            onChange={() => handleCheckboxChange('riskFactors', 'Substance intoxication')}
          />
          <FormCheckbox
            label="Abuse / neglect concerns"
            checked={omhcData.riskFactors?.includes('Abuse/neglect')}
            onChange={() => handleCheckboxChange('riskFactors', 'Abuse/neglect')}
          />
          <FormCheckbox
            label="Other safety concern"
            checked={omhcData.riskFactors?.includes('Other')}
            onChange={() => handleCheckboxChange('riskFactors', 'Other')}
          />
        </div>
        <div className="space-y-4 mt-4">
          <FormRadio
            label="Current Imminent Risk?"
            name="imminentRisk"
            options={['Yes', 'No']}
            value={omhcData.imminentRisk}
            onChange={(value) => handleChange('imminentRisk', value)}
            required
          />
          <FormRadio
            label="Recent Hospitalization (Last 30 Days)?"
            name="recentHospitalization"
            options={['Yes', 'No']}
            value={omhcData.recentHospitalization}
            onChange={(value) => handleChange('recentHospitalization', value)}
          />
          <FormRadio
            label="Mobile Crisis Involvement?"
            name="mobileCrisis"
            options={['Yes', 'No']}
            value={omhcData.mobileCrisis}
            onChange={(value) => handleChange('mobileCrisis', value)}
          />
          {(omhcData.imminentRisk === 'Yes' || 
            omhcData.riskFactors?.length > 1 || 
            omhcData.riskFactors?.includes('Suicidal ideation') ||
            omhcData.riskFactors?.includes('Homicidal ideation')) && (
            <FormTextArea
              label="Risk Details / Stabilization Plan"
              value={omhcData.riskDetails}
              onChange={(value) => handleChange('riskDetails', value)}
              rows={4}
              helperText="Describe current risk, protective factors, and any safety planning in place"
            />
          )}
        </div>
      </section>

      {/* OMHC Fit Assessment */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">
          OMHC Level of Care Assessment
        </h2>
        <div className="space-y-4">
          <FormRadio
            label="Appropriate for Outpatient Level of Care?"
            name="appropriateForOutpatient"
            options={['Yes', 'No']}
            value={omhcData.appropriateForOutpatient}
            onChange={(value) => handleChange('appropriateForOutpatient', value)}
            required
          />
          <FormRadio
            label="Requires Psychiatric Evaluation?"
            name="requiresPsychEval"
            options={['Yes', 'No']}
            value={omhcData.requiresPsychEval}
            onChange={(value) => handleChange('requiresPsychEval', value)}
          />
          <FormRadio
            label="Requires Ongoing Therapy?"
            name="requiresTherapy"
            options={['Yes', 'No']}
            value={omhcData.requiresTherapy}
            onChange={(value) => handleChange('requiresTherapy', value)}
          />
        </div>
      </section>
    </div>
  );
};

export default OMHCReferralForm;
